import { apiJson } from './http';

export type BulkFieldName =
  | 'name'
  | 'url'
  | 'logo'
  | 'epg_id'
  | 'shift'
  | 'category'
  | 'channel_number'
  | 'sort_order'
  | 'is_hidden'
  | 'sync_name'
  | 'catchup_days';

export type BulkFieldUpdate = { id: number; value: string | number | null };

export async function bulkSetField(payload: {
  playlist_id: number;
  field: BulkFieldName;
  updates: BulkFieldUpdate[];
}): Promise<{ success: boolean; message?: string }> {
  return apiJson('/api/channels/bulk_set_field.php', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function bulkSetDeleted(payload: {
  playlist_id: number;
  ids: number[];
  deleted: boolean;
}): Promise<{ success: boolean; message?: string }> {
  return apiJson('/api/channels/bulk_set_deleted.php', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export type UndoPayload =
  | {
      type: 'setField';
      playlist_id: number;
      field: BulkFieldName;
      valuesById: Record<string, string | number | null>;
    }
  | {
      type: 'setManyFields';
      playlist_id: number;
      updates: { field: BulkFieldName; valuesById: Record<string, string | number | null> }[];
    }
  | {
      type: 'setDeleted';
      playlist_id: number;
      ids: number[];
      deleted: boolean;
    };

export async function applyUndoPayload(payload: UndoPayload): Promise<void> {
  if (payload.type === 'setField') {
    const updates = Object.entries(payload.valuesById).map(([id, value]) => ({
      id: parseInt(id, 10),
      value,
    }));
    const res = await bulkSetField({
      playlist_id: payload.playlist_id,
      field: payload.field,
      updates,
    });
    if (!res.success) throw new Error(res.message || 'bulk_set_field failed');
    return;
  }
  if (payload.type === 'setManyFields') {
    const tasks = payload.updates
      .filter((u) => Boolean(u.field))
      .map((u) =>
        applyUndoPayload({
          type: 'setField',
          playlist_id: payload.playlist_id,
          field: u.field,
          valuesById: u.valuesById,
        }),
      );
    await Promise.all(tasks);
    return;
  }
  if (payload.type === 'setDeleted') {
    const res = await bulkSetDeleted({
      playlist_id: payload.playlist_id,
      ids: payload.ids,
      deleted: payload.deleted,
    });
    if (!res.success) throw new Error(res.message || 'bulk_set_deleted failed');
    return;
  }
  throw new Error('Unknown undo payload type');
}
